import { ArrowRightOutlined, LockOutlined } from "@ant-design/icons";
import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
const MAX_TITLE_LINES = 2;
const TILE_TEXT_LINES = 4;

export interface NavTile {
  key: string;
  title: string;
  description?: string;
  icon: ReactNode;
  locked?: boolean;
  badge?: string;
  onActivate: () => void;
}
export interface NavTileGroup { key: string; title?: ReactNode; items: NavTile[] }

/** Das gemeinsame Vierzeilenbudget richtet sich nach dem tatsächlichen Titelumbruch. */
function NavTileText({ title, description }: Pick<NavTile, "title" | "description">) {
  const titleRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const element = titleRef.current;
    if (!element) return;
    const measure = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0 || element.clientHeight === 0) return;
      const titleLines = Math.min(MAX_TITLE_LINES, Math.max(1, Math.round(element.clientHeight / lineHeight)));
      element.parentElement?.style.setProperty("--ck-tile-description-lines", String(TILE_TEXT_LINES - titleLines));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    // Auch ein nachgeladenes Theme-Font kann den Titelumbruch verändern.
    document.fonts.addEventListener("loadingdone", measure);
    return () => {
      observer.disconnect();
      document.fonts.removeEventListener("loadingdone", measure);
    };
  }, [title]);
  return <span className="ck-tile-text">
    <strong ref={titleRef}>{title}</strong>
    {description?.trim() ? <em>{description}</em> : null}
  </span>;
}

/**
 * Navigationskacheln des Kits (Einstellungen, Auswertungen, Bereichsübersichten). Darstellung im
 * Oberflächenstil (`app/DESIGN_SIGNATURE.md`); Bezeichnungen, Zugriff und Ziel liefert der Aufrufer.
 */
export function NavTiles({ groups, items }: { groups?: NavTileGroup[]; items?: NavTile[] }) {
  const renderTiles = (entries: NavTile[]) => (
    <div className="ck-tiles">
      {entries.map((item, index) => (
        <button key={item.key} type="button" className={`ck-tile${item.locked ? " is-locked" : ""}`} onClick={item.onActivate}
          aria-label={item.title} aria-description={item.description} title={item.description?.trim() ? `${item.title}\n${item.description}` : item.title}
          style={{ "--i": index } as CSSProperties}>
          <span className="ck-tile-icon" aria-hidden="true">{item.locked ? <LockOutlined /> : item.icon}</span>
          <NavTileText title={item.title} description={item.description} />
          {item.locked && item.badge ? <span className="ck-tile-badge">{item.badge}</span> : <ArrowRightOutlined className="ck-tile-go" aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
  if (!groups?.length) return <div className="ck-tile-groups">{renderTiles(items ?? [])}</div>;
  return (
    <div className="ck-tile-groups">
      {groups.map((group) => (
        <section key={group.key} className="ck-tile-group">
          {group.title ? <h2 className="ck-tile-group-title">{group.title}</h2> : null}
          {renderTiles(group.items)}
        </section>
      ))}
    </div>
  );
}
